import { useState, useCallback, useEffect } from 'react';
import { parseBlob } from 'music-metadata';

const LOCAL_STORAGE_PLAYLISTS_KEY = 'webPlayer_savedPlaylists_v2';

export function usePlaylist() {
  const [playlist, setPlaylistInternal] = useState([]); 
  const [currentIndex, setCurrentIndexInternal] = useState(0); 
  
  const [savedPlaylists, setSavedPlaylistsInternal] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_PLAYLISTS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) { console.error("Error loading saved playlists from localStorage:", e); return []; }
  });

  const setPlaylist = useCallback((newPlaylist) => {
    setPlaylistInternal(newPlaylist);
  }, []);

  const setCurrentIndex = useCallback((index) => {
    setCurrentIndexInternal(index);
  }, []);

  const setSavedPlaylists = useCallback((newSavedPlaylists) => {
    setSavedPlaylistsInternal(newSavedPlaylists);
    try {
      localStorage.setItem(LOCAL_STORAGE_PLAYLISTS_KEY, JSON.stringify(newSavedPlaylists));
    } catch (e) { console.error("Error saving playlists to localStorage:", e); }
  }, []);

  const addSongs = useCallback(async (filesArray) => {
    if (!filesArray || filesArray.length === 0) return playlist;
    
    const newSongsPromises = filesArray.map(async (file, idx) => {
      try {
        // Parse metadata to extract title, artist, album art, etc.
        // Performance optimization: parse only essential fields to reduce memory usage
        const metadata = await parseBlob(file, { 
          includeChapters: false,
          skipCovers: false, // We need covers for album art
          skipPostHeaders: true,
          duration: true
        });
        
        let albumArtUrl = null;
        if (metadata.common.picture && metadata.common.picture.length > 0) {
          // Performance optimization: use the first (usually highest quality) picture only
          const picture = metadata.common.picture[0];
          // Limit album art size to prevent memory issues
          if (picture.data && picture.data.length < 2 * 1024 * 1024) { // Max 2MB
            const blob = new Blob([picture.data], { type: picture.format });
            albumArtUrl = URL.createObjectURL(blob);
          }
        }

        const songDuration = metadata.format?.duration || 0;

        return {
          id: `${Date.now()}-${idx}-${Math.random().toString(36).substring(2, 15)}`,
          file: file, 
          name: file.name,
          title: metadata.common.title || file.name.replace(/\.[^/.]+$/, ''),
          artist: metadata.common.artist || 'Unknown Artist',
          album: metadata.common.album || 'Unknown Album',
          year: metadata.common.year || null,
          duration: songDuration, 
          albumArt: albumArtUrl, // legacy
          artwork: albumArtUrl,  // for player UI
          // Performance optimization: store only essential metadata to reduce memory
          metadata: {
            title: metadata.common.title,
            artist: metadata.common.artist,
            album: metadata.common.album,
            year: metadata.common.year,
            genre: metadata.common.genre?.[0], // Just first genre
            bitrate: metadata.format?.bitrate,
            sampleRate: metadata.format?.sampleRate
          },
          size: file.size,
          type: file.type,
          url: null,
          filePath: file.path || null, // Store file path if available (Electron)
        };
      } catch (error) {
        console.error(`❌ usePlaylist: Error processing file ${file.name}:`, error);
        return {
          id: `${Date.now()}-${idx}-${Math.random().toString(36).substring(2, 15)}`,
          file: file, name: file.name, title: file.name.replace(/\.[^/.]+$/, ''),
          artist: 'Unknown Artist', album: 'Unknown Album', year: null,
          duration: 0, albumArt: null, artwork: null, metadata: null,
          size: file.size, type: file.type, url: null,
          filePath: file.path || null, // Store file path if available (Electron)
        };
      }
    });

    const newSongs = await Promise.all(newSongsPromises);
    
    let finalUpdatedPlaylist;
    setPlaylistInternal(currentPlaylist => {
      const updatedPlaylist = [...currentPlaylist, ...newSongs];
      if (currentPlaylist.length === 0 && updatedPlaylist.length > 0) {
         setCurrentIndexInternal(0);
      }
      finalUpdatedPlaylist = updatedPlaylist;
      return updatedPlaylist;
    });
    
    return finalUpdatedPlaylist || [...playlist, ...newSongs]; 
  }, [playlist, setPlaylistInternal, setCurrentIndexInternal]);


  const removeSong = useCallback((indexToRemove) => {
    let newPlaylistState;
    setPlaylistInternal(prev => {
      const songToRemove = prev[indexToRemove];
      // Clean up album art blob URLs to prevent memory leaks
      // Both albumArt and artwork point to the same URL, so only revoke once
      const artworkUrl = songToRemove?.artwork || songToRemove?.albumArt;
      if (artworkUrl && artworkUrl.startsWith('blob:')) {
        URL.revokeObjectURL(artworkUrl);
      }
      newPlaylistState = prev.filter((_, index) => index !== indexToRemove);
      
      if (indexToRemove < currentIndex) {
        setCurrentIndexInternal(prevIdx => prevIdx - 1);
      } else if (indexToRemove === currentIndex) {
        if (newPlaylistState.length === 0) {
          setCurrentIndexInternal(0); 
        } else if (currentIndex >= newPlaylistState.length) {
          setCurrentIndexInternal(newPlaylistState.length - 1);
        }
      }
      return newPlaylistState;
    });
  }, [currentIndex, setCurrentIndexInternal, setPlaylistInternal]);

  const reorderSongs = useCallback((newOrder) => {
    // Handle both function and array inputs
    if (typeof newOrder === 'function') {
      // If it's a function, call it with the current playlist
      const result = newOrder(playlist);
      if (!Array.isArray(result)) {
        console.error('reorderSongs: function result is not an array:', result);
        return;
      }
      newOrder = result;
    } else if (!Array.isArray(newOrder)) {
      console.error('reorderSongs: newOrder is not an array or function:', newOrder);
      return;
    }
    
    const currentSongBeforeReorder = playlist[currentIndex];
    setPlaylistInternal(newOrder); 
    if (currentSongBeforeReorder) {
      const newIdx = newOrder.findIndex(song => song.id === currentSongBeforeReorder.id);
      setCurrentIndexInternal(newIdx >= 0 ? newIdx : (newOrder.length > 0 ? 0 : 0) );
    } else if (newOrder.length > 0) {
      setCurrentIndexInternal(0);
    } else {
      setCurrentIndexInternal(0);
    }
  }, [playlist, currentIndex, setPlaylistInternal, setCurrentIndexInternal]);

  const clearPlaylist = useCallback(() => {
    // Clean up all album art blob URLs to prevent memory leaks
    playlist.forEach(song => {
      const artworkUrl = song?.artwork || song?.albumArt;
      if (artworkUrl && artworkUrl.startsWith('blob:')) {
        URL.revokeObjectURL(artworkUrl);
      }
    });
    setPlaylistInternal([]);
    setCurrentIndexInternal(0);
  }, [playlist, setPlaylistInternal, setCurrentIndexInternal]);
  
  const savePlaylistToSaved = useCallback((name) => { 
    if (playlist.length === 0 || !name.trim()) return;
    const playlistToSave = {
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`,
      name: name.trim(),
      songs: playlist.map(song => ({
        id: song.id, name: song.name, title: song.title, artist: song.artist,
        album: song.album, year: song.year, duration: song.duration,
        size: song.size, type: song.type, 
      })),
      createdAt: Date.now()
    };
    setSavedPlaylists(prev => [...prev, playlistToSave]);
  }, [playlist, setSavedPlaylists]);

  const loadPlaylistFromSaved = useCallback((playlistId) => { 
    const playlistToLoad = savedPlaylists.find(p => p.id === playlistId);
    if (playlistToLoad) {
      const songsWithPlaceholders = playlistToLoad.songs.map(s => ({
        ...s, file: null, albumArt: null, url: null, metadata: null, 
      }));
      setPlaylistInternal(songsWithPlaceholders);
      setCurrentIndexInternal(songsWithPlaceholders.length > 0 ? 0 : 0);
    }
  }, [savedPlaylists, setPlaylistInternal, setCurrentIndexInternal]);

  const deleteSavedPlaylist = useCallback((playlistId) => {
    setSavedPlaylists(prev => prev.filter(p => p.id !== playlistId));
  }, [setSavedPlaylists]);

  return {
    playlist,
    currentIndex,
    setPlaylist: setPlaylistInternal, 
    setCurrentIndex: setCurrentIndexInternal,
    addSongs,
    removeSong,
    reorderSongs,
    savePlaylist: savePlaylistToSaved, 
    loadPlaylist: loadPlaylistFromSaved, 
    deleteSavedPlaylist,
    savedPlaylists,
    clearPlaylist
  };
}